



import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axios from 'axios';
import config from '../../Common/Configurations/APIConfig';
import DefaultData from '../../Common/CompanyData/DefaultData';

const CustomerAddCart = () => {
    const navigate = useNavigate();

    // 1. STATE MANAGEMENT
    const [cartItems, setCartItems] = useState(() => {
        const savedCart = localStorage.getItem('cart');
        try { return savedCart ? JSON.parse(savedCart) : []; } catch (e) { return []; }
    });

    const [lastOrderedItems, setLastOrderedItems] = useState([]); // For success summary
    const [currentUser, setCurrentUser] = useState(null);
    
    // Form States (Auto-filled via Profile API)
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [shippingAddress, setShippingAddress] = useState('');
    
    const [orderLoading, setOrderLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState('');
    const [orderDetails, setOrderDetails] = useState(null);

    const THEME_COLOR = '#065f46';
    const currency = DefaultData.DEFAULTCURRENCY || 'NGN';

    // 2. INITIAL LOAD: Fetch Profile to auto-fill email/phone/address
    useEffect(() => {
        const storedName = localStorage.getItem('name');
        const token = localStorage.getItem('userToken') || localStorage.getItem('token');
        const userId = localStorage.getItem('userId');

        if (token && userId) {
            setCurrentUser({ name: storedName || "Customer" });
            fetchUserProfile(token, userId);
        }
        window.scrollTo(0, 0);
    }, []);

    const fetchUserProfile = async (token, userId) => {
        try {
            // Using the exact pattern from your CustomerProfile.jsx
            const response = await axios.get(
                `${config.APIACTIVEURL}${config.GETUSERPROFILE}/${userId}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                const userData = response.data.data;
                setCustomerEmail(userData.email || '');
                setCustomerPhone(userData.phoneNumber || '');
                setShippingAddress(userData.address || '');
            }
        } catch (err) {
            console.error("Auto-fill profile failed:", err);
        }
    };

    // 3. CART ACTIONS
    const updateQuantity = (id, delta) => {
        const updated = cartItems.map(item => {
            if (item.id === id) {
                const newQty = Math.max(1, Math.min(item.availableQuantity || 99, item.quantity + delta));
                return { ...item, quantity: newQty };
            }
            return item;
        });
        setCartItems(updated);
        localStorage.setItem('cart', JSON.stringify(updated));
    };

    const removeItem = (id) => {
        const filtered = cartItems.filter(item => item.id !== id);
        setCartItems(filtered);
        localStorage.setItem('cart', JSON.stringify(filtered));
    };

    const calculateTotal = () => cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    const formatCurrency = (amt) => `${currency} ${Number(amt).toFixed(2)}`;

    // 4. CHECKOUT LOGIC
    const handleCheckout = async (e) => {
        e.preventDefault();
        setError('');

        if (!customerEmail.trim() || !shippingAddress.trim()) {
            setError('Please ensure Email and Shipping Address are provided');
            return;
        }

        setOrderLoading(true);

        try {
            const orderPayload = {
                Email: customerEmail.trim(),
                CustomerName: currentUser?.name || "Guest",
                PhoneNumber: customerPhone.trim(),
                ShippingAddress: shippingAddress.trim(),
                Status: "Order Placed",
                Items: cartItems.map(item => ({
                    ProductId: item.id,
                    Quantity: item.quantity
                }))
            };

            const response = await axios.post(config.APIACTIVEURL + config.CREATEORDER, orderPayload);

            if (response.data.success || response.status === 200) {
                setOrderDetails(response.data.data); 
                setLastOrderedItems([...cartItems]); // Save copy for Modal
                setShowSuccess(true);
                
                // Cleanup
                localStorage.removeItem('cart');
                setCartItems([]);
            } else {
                throw new Error(response.data.message || "Order failed");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Order failed. Please check your data.");
        } finally {
            setOrderLoading(false);
        }
    };

    return (
        <div style={{ backgroundColor: '#fafaf9', minHeight: '100vh' }} className="pt-5 mt-5 pb-5">
            <div className="container py-2">
                {/* Header */}
                <div className="d-flex align-items-center mb-4">
                    <button onClick={() => navigate(-1)} className="btn border-0 p-0 me-3 shadow-none">
                        <i className="ri-arrow-left-line fs-2"></i>
                    </button>
                    <h3 className="fw-bold mb-0" style={{ color: THEME_COLOR }}>Cart Summary</h3>
                </div>

                {!cartItems || cartItems.length === 0 ? (
                    <div className="text-center py-5 bg-white rounded-4 shadow-sm border">
                        <i className="ri-shopping-cart-line display-1 text-muted opacity-25"></i>
                        <h4 className="mt-3 text-muted">Your cart is empty</h4>
                        <Button onClick={() => navigate('/customer/products/search')} className="mt-3 rounded-pill" style={{backgroundColor: THEME_COLOR, border:'none'}}>
                            Start Shopping
                        </Button>
                    </div>
                ) : (
                    <div className="row g-4">
                        {/* LEFT: Cart List */}
                        <div className="col-lg-7">
                            <div className="card border-0 shadow-sm rounded-4 bg-white p-2">
                                {cartItems.map(item => (
                                    <div key={item.id} className="p-3 border-bottom text-start">
                                        <div className="row align-items-center g-2">
                                            <div className="col-3 col-md-2">
                                                <img src={item.image || 'https://placehold.co/70'} className="img-fluid rounded border" style={{ height: '60px', width: '60px', objectFit: 'contain' }} alt={item.name} />
                                            </div>
                                            <div className="col-9 col-md-4">
                                                <h6 className="fw-bold mb-0 small">{item.name}</h6>
                                                <p className="text-success small fw-bold mb-0">{formatCurrency(item.price)}</p>
                                            </div>
                                            <div className="col-8 col-md-4">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Button variant="light" className="rounded-circle p-0" style={{width:'25px', height:'25px'}} onClick={() => updateQuantity(item.id, -1)}>-</Button>
                                                    <span className="small fw-bold">{item.quantity}</span>
                                                    <Button variant="light" className="rounded-circle p-0" style={{width:'25px', height:'25px'}} onClick={() => updateQuantity(item.id, 1)}>+</Button>
                                                </div>
                                            </div>
                                            <div className="col-4 col-md-2 text-end">
                                                <button className="btn text-danger p-0" onClick={() => removeItem(item.id)}>
                                                    <i className="ri-delete-bin-line"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT: Checkout Form */}
                        <div className="col-lg-5">
                            <div className="card border-0 shadow-sm p-4 rounded-4 bg-white sticky-lg-top" style={{top: '120px'}}>
                                <h5 className="fw-bold mb-4">Checkout Details</h5>
                                <Form onSubmit={handleCheckout}>
                                    {error && <Alert variant="danger" className="small">{error}</Alert>}
                                    
                                    <Form.Group className="mb-3 text-start">
                                        <Form.Label className="small fw-bold">Customer Name</Form.Label>
                                        <Form.Control type="text" value={currentUser?.name || ''} readOnly disabled className="bg-light" />
                                    </Form.Group>

                                    <Form.Group className="mb-3 text-start">
                                        <Form.Label className="small fw-bold">Email Address *</Form.Label>
                                        <Form.Control 
                                            type="email" 
                                            placeholder="Fetching email..." 
                                            value={customerEmail} 
                                            onChange={e => setCustomerEmail(e.target.value)} 
                                            required 
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3 text-start">
                                        <Form.Label className="small fw-bold">Phone Number</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder="Enter phone" 
                                            value={customerPhone} 
                                            onChange={e => setCustomerPhone(e.target.value)} 
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-4 text-start">
                                        <Form.Label className="small fw-bold">Shipping Address *</Form.Label>
                                        <Form.Control as="textarea" rows={2} placeholder="Address" value={shippingAddress} onChange={e => setShippingAddress(e.target.value)} required />
                                    </Form.Group>

                                    <div className="d-flex justify-content-between mb-4 border-top pt-3">
                                        <span className="fw-bold">Total Payable</span>
                                        <span className="fw-bold text-success h5">{formatCurrency(calculateTotal())}</span>
                                    </div>
                                    <Button type="submit" className="w-100 py-3 fw-bold text-white border-0 rounded-pill shadow-sm" style={{ backgroundColor: THEME_COLOR }} disabled={orderLoading}>
                                        {orderLoading ? 'Processing...' : 'Place Order'}
                                    </Button>
                                </Form>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* SUCCESS MODAL */}
            <Modal show={showSuccess} onHide={() => navigate('/customer/orders')} centered backdrop="static" size="lg">
                <Modal.Body className="p-4 p-md-5">
                    <div className="text-center mb-4">
                        <i className="ri-checkbox-circle-fill text-success display-3 mb-2"></i>
                        <h2 className="fw-bold">Order Confirmed!</h2>
                        {/* Displaying Order No from API Data */}
                        <h5 className="text-muted">
                            Order No: <span className="text-dark">#{orderDetails?.orderNo || orderDetails?.orderNumber || orderDetails?.id || 'N/A'}</span>
                        </h5>
                    </div>

                    <div className="border rounded-4 p-4 bg-light mb-4 text-start">
                        <h6 className="fw-bold border-bottom pb-2 mb-3">Summary</h6>
                        {lastOrderedItems.map((item, idx) => (
                            <div key={idx} className="d-flex justify-content-between align-items-center mb-2 small">
                                <span>{item.name} <span className="text-muted">x{item.quantity}</span></span>
                                <span className="fw-bold">{formatCurrency(item.price * item.quantity)}</span>
                            </div>
                        ))}
                        <div className="d-flex justify-content-between border-top pt-2 mt-2 fw-bold text-success">
                            <span>Total Paid</span>
                            <span>{formatCurrency(lastOrderedItems.reduce((acc, i) => acc + (i.price * i.quantity), 0))}</span>
                        </div>
                    </div>

                    <div className="d-grid gap-2">
                        <Button onClick={() => navigate('/customer/orders')} className="py-3 text-white border-0 rounded-pill shadow-sm" style={{ backgroundColor: THEME_COLOR }}>
                            View My Orders
                        </Button>
                        <Button variant="light" onClick={() => navigate('/')} className="py-2 rounded-pill border-0 text-muted">
                            Continue Shopping
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default CustomerAddCart;